import { useCallback, useEffect, useState } from "react";
import axios from "axios";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const KEY = (slug) => `collaborator_token_${slug}`;

/**
 * Manages the long-lived opaque collaborator token for an interactive gallery.
 * Token lives in localStorage, scoped per-slug. `me` is hydrated on mount
 * (and on `refresh()`) by calling /me — a 401 there clears the local token
 * and forces a re-gate.
 *
 * Returns:
 *   token        — current bearer token (or "" if not authed)
 *   me           — { id, gallery_id, display_name, role, email, … } | null
 *   role         — convenience: me?.role
 *   displayName  — convenience: me?.display_name
 *   loading      — true while bootstrapping
 *   setToken(t)  — persist a freshly-issued token and re-hydrate
 *   clear()      — log out (delete token from storage)
 *   refresh()    — re-pull /me with the current token
 */
export default function useCollaborator(slug) {
  const [token, setTokenState] = useState(() => {
    try { return localStorage.getItem(KEY(slug)) || ""; } catch { return ""; }
  });
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (overrideToken) => {
    const t = overrideToken ?? token;
    if (!t) { setMe(null); setLoading(false); return null; }
    try {
      const { data } = await axios.get(`${API}/galleries/${slug}/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      setMe(data);
      setLoading(false);
      return data;
    } catch (err) {
      // Any auth failure → wipe & force gate.
      if (err.response?.status === 401) {
        try { localStorage.removeItem(KEY(slug)); } catch (e) { void e; }
        setTokenState("");
      }
      setMe(null);
      setLoading(false);
      return null;
    }
  }, [slug, token]);

  // Bootstrap on mount / when slug changes.
  useEffect(() => {
    setLoading(true);
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const setToken = useCallback(async (t) => {
    try { localStorage.setItem(KEY(slug), t); } catch (e) { void e; }
    setTokenState(t);
    await refresh(t);
  }, [slug, refresh]);

  const clear = useCallback(() => {
    try { localStorage.removeItem(KEY(slug)); } catch (e) { void e; }
    setTokenState("");
    setMe(null);
  }, [slug]);

  return {
    token,
    me,
    role: me?.role || null,
    displayName: me?.display_name || "",
    loading,
    setToken,
    clear,
    refresh,
  };
}

export const collaboratorAuthHeader = (token) =>
  token ? { Authorization: `Bearer ${token}` } : {};
